/**
 * Modale d'historique des modifications de notes de devoirs (2026-08-27).
 *
 * Ouverte par le bouton [🕒 Historique] du bandeau de contexte de la page
 * Saisie Notes. Filtre par défaut sur le contexte courant : matière + classe
 * + semestre + année académique. L'agent peut affiner par plage de dates,
 * étudiant, agent ayant modifié, ou champ (D1/D2).
 *
 * Colonnes : Date/Heure, Agent, Étudiant, Matricule, Champ, Ancienne →
 * Nouvelle valeur, Motif, IP.
 *
 * Endpoint : GET /api/historique-modifications-notes/
 * (filtres supportés : matiere_id, classe, semestre, annee_academique,
 * etudiant_id, matricule, modifie_par, champ, date_debut, date_fin)
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Download, Filter, Search, Clock, User, RefreshCw } from 'lucide-react';
import { useGetHistoriqueQuery } from '../../services/notesDevoirsApi';
import type { HistoriqueModification } from '../../services/notesDevoirsApi';

interface Props {
  open: boolean;
  onClose: () => void;
  matiereId?: number;
  matiereLabel?: string;
  classe?: string;
  semestre?: string;
  anneeAcademique?: string;
  /** Si fourni : historique restreint à CETTE note uniquement (accès clic droit). */
  noteDevoirId?: number;
  /** Contexte étudiant affiché en titre quand on cible une seule note. */
  etudiantLabel?: string;
}

const HistoriqueSaisieModal: React.FC<Props> = ({
  open, onClose, matiereId, matiereLabel, classe, semestre, anneeAcademique,
  noteDevoirId, etudiantLabel,
}) => {
  const [matriculeFiltre, setMatriculeFiltre] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [champ, setChamp] = useState<string>('');
  const [agentQuery, setAgentQuery] = useState('');

  // Mode "note unique" (clic droit) → on charge SEULEMENT l'historique de cette note.
  // Mode "global" → on filtre par matière + classe + sem + année.
  const isNoteMode = !!noteDevoirId;

  const { data: items = [], isFetching, refetch } = useGetHistoriqueQuery(
    isNoteMode
      ? { note_devoir_id: noteDevoirId, page_size: 100 }
      : {
          matiere_id: matiereId,
          classe: classe || undefined,
          semestre: semestre || undefined,
          annee_academique: anneeAcademique || undefined,
          matricule: matriculeFiltre.trim() || undefined,
          champ: champ || undefined,
          date_debut: dateDebut || undefined,
          date_fin: dateFin || undefined,
          page_size: 1000,
        },
    { skip: !open },
  );

  // Filtre agent côté client (nom/username contient la query)
  const filtered = useMemo(() => {
    const q = agentQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(l => {
      const u = (l.modifie_par_username || '').toLowerCase();
      const n = (l.modifie_par_nom_complet || '').toLowerCase();
      return u.includes(q) || n.includes(q);
    });
  }, [items, agentQuery]);

  const exportCSV = () => {
    if (!filtered.length) return;
    const header = ['Date', 'Heure', 'Agent', 'Étudiant', 'Matricule', 'Champ', 'Ancienne', 'Nouvelle', 'Motif', 'IP'];
    const rows = filtered.map(l => {
      const d = new Date(l.date_modification);
      const date = d.toLocaleDateString('fr-FR');
      const heure = d.toLocaleTimeString('fr-FR');
      const agent = (l.modifie_par_nom_complet || l.modifie_par_username || '—').replace(/"/g, '""');
      const nomEtu = `${l.etudiant_nom || ''} ${l.etudiant_prenoms || ''}`.trim();
      return [
        date, heure, `"${agent}"`, `"${nomEtu}"`, l.etudiant_matricule,
        l.champ_modifie_display || l.champ_modifie,
        `"${(l.ancienne_valeur ?? '—').replace(/"/g, '""')}"`,
        `"${(l.nouvelle_valeur ?? '—').replace(/"/g, '""')}"`,
        `"${(l.motif_display || l.motif).replace(/"/g, '""')}"`,
        l.adresse_ip || '',
      ].join(',');
    });
    const blob = new Blob(['﻿' + [header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historique_notes_${matiereLabel || 'matiere'}_${classe || ''}_${semestre || ''}.csv`.replace(/[^a-zA-Z0-9_\-\.]/g, '_');
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open || typeof document === 'undefined') return null;

  // Rendu via Portal directement dans <body> pour éviter les problèmes de
  // stacking context / scroll (fix 2026-08-27 : ancienne modale s'affichait
  // hors écran quand l'utilisateur avait scrollé en bas de la table).
  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div className="w-full max-w-6xl max-h-[92vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
              <Clock size={18} className="text-blue-500" />
              {isNoteMode ? 'Historique de cette note' : 'Historique des modifications'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {isNoteMode && etudiantLabel && <><strong>{etudiantLabel}</strong> · </>}
              {matiereLabel || 'Matière ?'}
              {classe && <> · {classe}</>}
              {semestre && <> · {semestre}</>}
              {anneeAcademique && <> · {anneeAcademique}</>}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => refetch()} disabled={isFetching}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
              title="Rafraîchir">
              <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            </button>
            <button onClick={exportCSV} disabled={!filtered.length}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
              title="Exporter en CSV">
              <Download size={16} />
            </button>
            <button onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Fermer">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Filtres — masqués en mode note unique (1 note = pas besoin de filtrer) */}
        {!isNoteMode && (
          <div className="px-4 md:px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="flex items-center gap-2 mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">
              <Filter size={12} /> Filtres
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <input value={matriculeFiltre} onChange={(e) => setMatriculeFiltre(e.target.value)}
                placeholder="Matricule étudiant" className={inp} />
              <input value={agentQuery} onChange={(e) => setAgentQuery(e.target.value)}
                placeholder="Agent (nom / username)" className={inp} />
              <select value={champ} onChange={(e) => setChamp(e.target.value)} className={inp}>
                <option value="">Champ : tous</option>
                <option value="note1">Note D.1</option>
                <option value="note2">Note D.2</option>
                <option value="absent1">Absent D.1</option>
                <option value="absent2">Absent D.2</option>
              </select>
              <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                className={inp} title="Depuis" />
              <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)}
                className={inp} title="Jusqu'à" />
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="flex-1 overflow-auto">
          {isFetching && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Chargement…
            </div>
          )}
          {!isFetching && filtered.length === 0 && (
            <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
              <Search className="mx-auto mb-2 opacity-40" size={28} />
              Aucune modification trouvée pour ce contexte.
            </div>
          )}
          {!isFetching && filtered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0 z-10">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  <th className="px-3 py-2 font-medium">Date / Heure</th>
                  <th className="px-3 py-2 font-medium">Agent</th>
                  <th className="px-3 py-2 font-medium">Étudiant</th>
                  <th className="px-3 py-2 font-medium">Champ</th>
                  <th className="px-3 py-2 font-medium text-center">Ancienne</th>
                  <th className="px-3 py-2 font-medium text-center">Nouvelle</th>
                  <th className="px-3 py-2 font-medium">Motif</th>
                  <th className="px-3 py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filtered.map(l => <LigneHistorique key={l.id} l={l} />)}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 md:px-5 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-400 flex items-center justify-between">
          <div>{filtered.length} modification{filtered.length > 1 ? 's' : ''}</div>
          <div className="italic">Chaque modification est journalisée définitivement.</div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const LigneHistorique: React.FC<{ l: HistoriqueModification }> = ({ l }) => {
  const d = new Date(l.date_modification);
  const dateStr = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const heureStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const nomEtu = `${l.etudiant_nom || ''} ${l.etudiant_prenoms || ''}`.trim() || '—';
  const agent = l.modifie_par_nom_complet || l.modifie_par_username || '—';

  const isD1 = l.champ_modifie === 'note1' || l.champ_modifie === 'absent1';
  const isAbs = l.champ_modifie.startsWith('absent');

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
        <div>{dateStr}</div>
        <div className="text-xs text-gray-500">{heureStr}</div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center gap-1 text-gray-900 dark:text-gray-100">
          <User size={12} className="text-gray-400" /> {agent}
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="text-gray-900 dark:text-gray-100">{nomEtu}</div>
        <div className="text-xs text-gray-500 font-mono">{l.etudiant_matricule || '—'}</div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
          isAbs
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
            : isD1
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
        }`}>
          {l.champ_modifie_display || l.champ_modifie}
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-center text-gray-500 font-mono">
        {l.ancienne_valeur ?? '—'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-center font-mono font-semibold text-gray-900 dark:text-gray-100">
        {l.nouvelle_valeur ?? '—'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
        {l.motif_display || l.motif}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 font-mono">
        {l.adresse_ip || '—'}
      </td>
    </tr>
  );
};

const inp = 'w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

export default HistoriqueSaisieModal;
